import React, { useState, useRef } from 'react'
import styled from '@emotion/styled'
import { Button, Carousel, Checkbox, message, Modal } from 'antd'
import { UPDATE_MARKETING_MODAL_VIEWERS_SETTINGS } from './MarketingQueries'
import { useSession } from '@slerp/accounts'
import { useMutation, useApolloClient } from '@apollo/client'
import { useGlobalQuery } from 'hooks/useGlobalQuery'

import Carousel1 from '@slerp/assets/crm-1.jpg'
import Carousel2 from '@slerp/assets/crm-2.jpg'
import Carousel3 from '@slerp/assets/crm-3.jpg'
import Carousel4 from '@slerp/assets/crm-4.jpg'
import { useMerchant } from 'hooks/useGlobalContext/useMerchant'

interface ItemAgenda {
  title: string
  description: string
}

interface CarouselItemType {
  caption: string
  subcaption: string
  imageSource: string
  callToActionSource: string
  callToActionLabel: string
  agenda?: ItemAgenda[]
}

// make this configurable in the future
const CAROUSEL_ITEMS = [
  {
    caption: 'Drive more repeat business with Slerp CRM',
    subcaption:
      'Nearly 2/3 of customers who’ve ordered 3 times will do so again within 6 months.',
    imageSource: Carousel1,
    callToActionSource: 'https://www.slerp.com/crm-book-a-demo/',
    callToActionLabel: 'Book a demo'
  },
  {
    caption: 'Turn your customers into regulars',
    subcaption:
      'Start using your data to personalise your messaging and keep customers coming back.',
    imageSource: Carousel2,
    callToActionSource: 'https://www.slerp.com/crm-book-a-demo/',
    callToActionLabel: 'Book a demo'
  },
  {
    caption: 'All your customer data in one place',
    subcaption:
      'Effortlessly sync all your online ordering, table booking and wifi data.',
    imageSource: Carousel3,
    callToActionSource: 'https://www.slerp.com/crm-book-a-demo/',
    callToActionLabel: 'Book a demo'
  },
  {
    caption: 'Automated SMS & Email',
    subcaption:
      'We’ll help you set up effective automations that will generate more repeat orders with no day-to-day effort from you or your team.',
    imageSource: Carousel4,
    callToActionSource: 'https://www.slerp.com/crm-book-a-demo/',
    callToActionLabel: 'Book a demo'
  }
]

const MarketingInitiativeModal = () => {
  const client = useApolloClient()
  const { merchant, user } = useSession()
  const { isLoading } = useGlobalQuery()
  const { merchantData } = useMerchant()
  const [currentSlide, setCurrentSlide] = useState<number>(0)
  const [showModal, setShowModal] = useState<boolean>(true)

  const slider = useRef(null)
  const skipModalCheckbox = useRef(null)

  const isLastSlide = currentSlide === CAROUSEL_ITEMS.length - 1
  const closeModalLabel = isLastSlide ? 'FINISH' : 'SKIP'

  const [updateMarketingModalViewersSettings] = useMutation(
    UPDATE_MARKETING_MODAL_VIEWERS_SETTINGS,
    {
      fetchPolicy: 'no-cache'
    }
  )

  const handleSkipModal = () => {
    // @ts-ignore
    const skipModalForGood = skipModalCheckbox.current?.input?.checked

    if (!skipModalForGood) {
      setShowModal(false)
      return
    }

    if (skipModalForGood) {
      const userIds = [
        ...(merchantData?.setting?.marketing_modal_viewers || []),
        user?.id
      ]
      updateMarketingModalViewersSettings({
        variables: {
          merchantId: merchant?.id,
          setting: {
            marketing_modal_viewers: userIds
          }
        }
      })
        .then(() => {
          client.resetStore()
        })
        .finally(() => setShowModal(false))
        .catch((error: Error) => {
          message.destroy()
          message.error(
            `Unable to toggle hide marketing initiative modal due to ${error.message}`,
            3
          )
        })
    }
  }

  const ModalFooter = [
    <Checkbox
      ref={skipModalCheckbox}
      key='checkbox'
      disabled={isLoading}
      data-testid='marketing-checkbox'
    >
      Don't show again
    </Checkbox>,
    <span key='buttons'>
      <Button
        key='skip'
        type='ghost'
        className='-secondary'
        onClick={handleSkipModal}
        data-testid='marketing-skip-btn'
      >
        {closeModalLabel}
      </Button>
      {!isLastSlide && (
        <Button
          key='next'
          // @ts-ignore
          onClick={() => slider.current?.next()}
          data-testid='marketing-next-btn'
        >
          NEXT
        </Button>
      )}
    </span>
  ]

  const handleBeforeChangeSlide = (fromSlide: number, toSlide: number) =>
    setCurrentSlide(toSlide)

  return (
    <StyledMarketingModal
      open={showModal}
      closable={false}
      footer={ModalFooter}
      width={665}
    >
      <Carousel
        ref={slider}
        effect='fade'
        beforeChange={handleBeforeChangeSlide}
      >
        {CAROUSEL_ITEMS.map((item: CarouselItemType, index: number) => {
          return (
            <div key={index} className='carousel-item _mb-0'>
              <div className='carousel-caption'>{item.caption}</div>
              <img
                className='carousel-screen small-carousel-image'
                src={item.imageSource}
                alt='Carousel item'
                style={{
                  marginTop: '24px',
                  ...(index === 2 && { marginBottom: '24px' })
                }}
              />
              <div
                className='carousel-subcaption'
                style={{
                  margin: '8px 0 16px 0'
                }}
              >
                {item.subcaption}
              </div>

              {item.callToActionSource && (
                <Button
                  className='carousel-cta-button'
                  shape='round'
                  size='large'
                  type='primary'
                  href={item.callToActionSource}
                  target='_blank'
                  rel='noopener noreferrer'
                  style={{
                    transform: 'scale(1)',
                    marginBottom: '40px'
                  }}
                >
                  {item.callToActionLabel}
                </Button>
              )}
            </div>
          )
        })}
      </Carousel>
    </StyledMarketingModal>
  )
}

const StyledMarketingModal = styled(Modal)`
  .ant-carousel .slick-dots li button {
    background: #4a4a68;
    opacity: 0.4;
    width: 10px;
    height: 10px;
    border-radius: 5px;
  }

  .ant-carousel .slick-dots li.slick-active button {
    opacity: 1;
    background: #4a4a68;
  }

  .ant-carousel .slick-dots li.slick-active {
    width: 16px;
  }

  .ant-carousel .slick-dots-bottom {
    bottom: 0;
  }

  .ant-modal-footer {
    display: flex;
    text-align: justify;
    justify-content: space-between;
  }

  .carousel-item {
    text-align: center;
    padding: 1em 2em 0 2em;

    display: flex !important;
    flex-flow: column;
    align-items: center;

    .carousel-caption {
      font-weight: 700;
      font-size: 24px;
    }

    .carousel-subcaption {
      font-size: 20px;
      width: 36ch;

      font-weight: 300;
    }

    .carousel-screen {
      margin-left: auto;
      margin-right: auto;
      margin-top: 2em;
      margin-bottom: 2em;
    }

    .small-carousel-image {
      height: 240px;
    }

    .carousel-cta-button {
      cursor: pointer;
      > span {
        text-transform: uppercase;
      }
    }
  }
`

export default MarketingInitiativeModal
